import { Input } from "../../../components/ui";
import type { EmpresaConfiguracion, EmpresaConfiguracionUpdate } from "../api";
import { SaveBar, nullIfEmpty, useConfigForm } from "./_shared";

interface Form {
  nombre: string;
  direccion: string;
  telefono: string;
  telefono_emergencia: string;
  sitio_web: string;
  giro: string;
  logo_url: string;
}

const extractor = (cfg: EmpresaConfiguracion): Form => ({
  nombre: cfg.nombre,
  direccion: cfg.direccion ?? "",
  telefono: cfg.telefono ?? "",
  telefono_emergencia: cfg.telefono_emergencia ?? "",
  sitio_web: cfg.sitio_web ?? "",
  giro: cfg.giro ?? "",
  logo_url: cfg.logo_url ?? "",
});

const differ = (
  f: Form,
  cfg: EmpresaConfiguracion,
): EmpresaConfiguracionUpdate => {
  const out: EmpresaConfiguracionUpdate = {};
  if (f.nombre.trim() !== cfg.nombre) out.nombre = f.nombre.trim();
  if (nullIfEmpty(f.direccion) !== cfg.direccion)
    out.direccion = nullIfEmpty(f.direccion);
  if (nullIfEmpty(f.telefono) !== cfg.telefono)
    out.telefono = nullIfEmpty(f.telefono);
  if (nullIfEmpty(f.telefono_emergencia) !== cfg.telefono_emergencia)
    out.telefono_emergencia = nullIfEmpty(f.telefono_emergencia);
  if (nullIfEmpty(f.sitio_web) !== cfg.sitio_web)
    out.sitio_web = nullIfEmpty(f.sitio_web);
  if (nullIfEmpty(f.giro) !== cfg.giro) out.giro = nullIfEmpty(f.giro);
  if (nullIfEmpty(f.logo_url) !== cfg.logo_url)
    out.logo_url = nullIfEmpty(f.logo_url);
  return out;
};

export function IdentificacionTab({
  cfg,
}: {
  cfg: EmpresaConfiguracion;
}): JSX.Element {
  const { form, setForm, haycambios, pending, reset, save } = useConfigForm(
    cfg,
    extractor,
    differ,
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-neutral-600">
        Datos institucionales del comité. RUT y email se cambian desde la
        Console SaaS.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Nombre"
          required
          type="text"
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
        />
        <Input
          label="RUT"
          hint="solo lectura"
          type="text"
          value={cfg.rut}
          readOnly
          className="bg-neutral-50"
        />
        <Input
          label="Email"
          hint="solo lectura"
          type="text"
          value={cfg.email}
          readOnly
          className="bg-neutral-50"
        />
        <Input
          label="Giro"
          type="text"
          value={form.giro}
          onChange={(e) => setForm({ ...form, giro: e.target.value })}
        />
        <Input
          label="Dirección"
          containerClassName="sm:col-span-2"
          type="text"
          value={form.direccion}
          onChange={(e) => setForm({ ...form, direccion: e.target.value })}
        />
        <Input
          label="Teléfono"
          type="text"
          value={form.telefono}
          onChange={(e) => setForm({ ...form, telefono: e.target.value })}
          placeholder="+56 9 1234 5678"
        />
        <Input
          label="Teléfono emergencia"
          type="text"
          value={form.telefono_emergencia}
          onChange={(e) =>
            setForm({ ...form, telefono_emergencia: e.target.value })
          }
          placeholder="+56 9 8765 4321"
        />
        <Input
          label="Sitio web"
          containerClassName="sm:col-span-2"
          type="text"
          value={form.sitio_web}
          onChange={(e) => setForm({ ...form, sitio_web: e.target.value })}
          placeholder="https://..."
        />
        <div className="sm:col-span-2">
          <div className="flex items-start gap-3">
            <Input
              label="URL del logo"
              hint="aparece en la cabecera de la boleta"
              containerClassName="flex-1"
              type="text"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://...archivos/logo-comite.png"
            />
            {form.logo_url.trim() && (
              <div className="shrink-0 mt-6 h-16 w-16 rounded-md ring-1 ring-neutral-200 bg-neutral-50 grid place-items-center overflow-hidden">
                <img
                  src={form.logo_url}
                  alt="Logo del comité"
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            PNG/JPG/WEBP, máximo 1 MB. Usa una URL pública (Cloudinary,
            Imgur, S3 público, etc.). Para el preview de la boleta el
            backend descarga la imagen y la cachea por 1 hora.
          </p>
        </div>
      </div>

      <SaveBar
        haycambios={haycambios}
        pending={pending}
        reset={reset}
        save={save}
      />
    </div>
  );
}
